import { z } from "zod";
import { daysBetweenLocalDates } from "./vaccination-interval.js";
/**
 * Vaccination due-date alerting state machine
 * (`docs/products/stable.md` "Vaccinations": "escalating sequence: a first
 * warning at a configurable ~30 days out, a nudge at a configurable ~7 days
 * out, then a persistent overdue flag on the horse that stays until
 * resolved").
 *
 * This module deliberately separates two things that are easy to conflate:
 *
 * 1. **The flag shown on the horse** ({@link vaccinationAlertSeverity}) — a
 *    pure, real-time function of `next_due` and "today". It is never stored;
 *    it is recomputed on every read. This is what makes "stays until
 *    resolved" trivially correct: the severity is OVERDUE for as long as
 *    `next_due` is in the past, and the only thing that ever changes
 *    `next_due` is a new dose being logged (`docs/products/stable.md`:
 *    "Logging a new dose resets the clock"). There is no stored "overdue"
 *    boolean to forget to clear.
 * 2. **The notification event** ({@link nextAlertToSend}) — a *transition*
 *    detector. A daily scan computes the current severity and compares it
 *    against the severity it last alerted for the horse's *current*
 *    `next_due` cycle (tracked in `stable_vaccination_alert_states`,
 *    `packages/database`). It emits a notification only when severity has
 *    newly escalated (or jumped straight to a higher stage after a missed
 *    scan) for this cycle, never merely because severity is still the same
 *    non-NONE value it was yesterday — this is what prevents "OVERDUE" from
 *    being emitted every single day it remains true.
 *
 * A cycle is keyed by `next_due` itself: once a new dose is logged,
 * `next_due` changes, which the scan detects as a new cycle and resets
 * tracking from scratch — "logging a new dose resets the clock" applies to
 * the notification sequence as much as to the flag.
 *
 * **Acknowledgment** (`docs/products/stable.md`: "Logging a scheduled 'due'
 * entry counts as acknowledged and suppresses the nudge for that cycle") only
 * gates the *notification*, never the *flag*: a vet booking the next dose
 * ahead of time stops further pestering, but the horse's displayed status
 * stays accurate (still shows overdue if it genuinely is) because the flag
 * is informational only and never blocks a capability
 * (`canAddSealedRecord` is untouched by any of this). Acknowledgment is
 * itself scoped to the current cycle by construction: the only way to end it
 * is a new dose, which starts a new cycle anyway.
 */
export const VACCINATION_ALERT_SEVERITIES = [
    "NONE",
    "WARNING",
    "NUDGE",
    "OVERDUE",
];
export const VaccinationAlertSeveritySchema = z.enum(VACCINATION_ALERT_SEVERITIES);
/**
 * The horse-facing flag: a pure function of `nextDue` and `today`, with no
 * stored state. `nextDue === null` (no dose, or a dose whose vaccine type has
 * no configured interval and was not overridden) always returns `NONE` —
 * "a horse with no dose and no entered last-dose date has no next_due and
 * never false-alarms."
 */
export function vaccinationAlertSeverity(input) {
    if (input.nextDue === null)
        return "NONE";
    const daysUntilDue = daysBetweenLocalDates(input.today, input.nextDue);
    if (daysUntilDue < 0)
        return "OVERDUE";
    if (daysUntilDue <= input.nudgeLeadDays)
        return "NUDGE";
    if (daysUntilDue <= input.warningLeadDays)
        return "WARNING";
    return "NONE";
}
/** Maps an escalation stage to the outbox event it fires — `NONE` never
 * fires anything. */
export const VACCINATION_ALERT_EVENT_TYPES = {
    WARNING: "VACCINATION_DUE_SOON",
    NUDGE: "VACCINATION_NUDGE",
    OVERDUE: "VACCINATION_OVERDUE",
};
/**
 * A scheduled "due" timeline entry (`dueDate` set, no outcome yet) for this
 * horse+vaccine-type counts as acknowledgment only if it was created after
 * the current cycle's dose — an appointment booked for a *previous* cycle
 * (now stale, since a new dose has since been logged) does not silently
 * suppress the new cycle's alerts.
 */
export function isAcknowledgedForCycle(input) {
    if (input.latestOpenScheduledEntryAt === null)
        return false;
    if (input.latestDoseLoggedAt === null)
        return true;
    return input.latestOpenScheduledEntryAt > input.latestDoseLoggedAt;
}
/**
 * The full per-cycle decision for one horse+vaccine-type on one scan run.
 * Pure and total: given today's inputs and yesterday's tracked state, it
 * decides whether to emit a notification and what the tracked state should
 * become — the daily scan use case is a thin loop around this function plus
 * I/O.
 *
 * - A cycle change (`tracked.nextDue !== nextDue` — a new dose was logged
 *   since the last scan) discards the stale tracked severity and evaluates
 *   fresh, so a horse that was OVERDUE last cycle does not suppress this
 *   cycle's first WARNING.
 * - Severity is monotonically non-decreasing within a cycle (today only
 *   moves forward and `nextDue` is fixed for the cycle), so "severity
 *   differs from what was last alerted" is equivalent to "severity has
 *   escalated" — this is what makes an equality check sufficient to prevent
 *   duplicate sends without needing an explicit rank comparison.
 * - Acknowledgment suppresses the send but still advances the tracked
 *   severity, so un-acknowledging is never possible mid-cycle (there is no
 *   "cancel a scheduled entry" action in this phase) and no backlog of
 *   suppressed sends accumulates to fire later.
 */
export function decideVaccinationAlert(input) {
    const severity = vaccinationAlertSeverity(input);
    const sameCycle = input.tracked !== null && input.tracked.nextDue === input.nextDue;
    const lastAlertedSeverity = sameCycle
        ? (input.tracked?.lastAlertedSeverity ?? "NONE")
        : "NONE";
    if (severity === "NONE") {
        return {
            decision: { action: "NONE" },
            nextTrackedState: { nextDue: input.nextDue, lastAlertedSeverity: "NONE" },
        };
    }
    if (input.acknowledged) {
        return {
            decision: { action: "SUPPRESSED_ACKNOWLEDGED" },
            nextTrackedState: {
                nextDue: input.nextDue,
                lastAlertedSeverity: severity,
            },
        };
    }
    if (severity === lastAlertedSeverity) {
        return {
            decision: { action: "NONE" },
            nextTrackedState: {
                nextDue: input.nextDue,
                lastAlertedSeverity: severity,
            },
        };
    }
    return {
        decision: { action: "SEND", severity },
        nextTrackedState: { nextDue: input.nextDue, lastAlertedSeverity: severity },
    };
}
